import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  filter,
  interval,
  map,
  Observable,
  of,
  skip,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  timeout,
  timer,
} from 'rxjs';
import { AppService } from 'src/app/app.service';
import { HistoryApi } from 'src/app/shared/apis/history.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { HasCryptoDepositParam } from 'src/app/shared/interfaces/history.interface';
import { ResponseData } from 'src/app/shared/interfaces/response.interface';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { SignalrService } from 'src/app/shared/service/signalr.service';
import { WalletDetailsData, WalletService } from '../wallet.service';

enum TranStatus {
  /** 交易已發送 */
  TRANSACTION_SENT,
  /** 驗證您的存款中 */
  VERIFYING,
  /** 存款已入帳 */
  CREDITED,
  /** 成功 */
  SUCCESSFUL,
  /** 失敗 */
  FAILED,
  /** 超時 */
  TIMEOUT_ERROR,
}

@UntilDestroy()
@Component({
  selector: 'app-deposit-transaction-details',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, CSButtonComponent, PipesModule],
  templateUrl: './deposit-transaction-details.component.html',
  styleUrl: './deposit-transaction-details.component.scss',
})
export class DepositTransactionDetailsComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private appService: AppService,
    private signalrService: SignalrService,
    private walletService: WalletService,
    private historyApi: HistoryApi,
  ) {}

  protected item = signal<WalletDetailsData | null>(null);

  protected readonly TranStatus = TranStatus;
  protected transactionStatus = signal(TranStatus.TRANSACTION_SENT);
  private transactionStatus$ = toObservable(this.transactionStatus);

  protected transactionProcess = signal(25);

  protected title = computed(() => {
    switch (this.transactionStatus()) {
      case TranStatus.SUCCESSFUL:
        return 'Deposit successful';

      case TranStatus.FAILED:
        return 'Deposit failed';

      default:
        return 'Deposit in progress';
    }
  });

  protected content = computed(() => {
    switch (this.transactionStatus()) {
      case TranStatus.SUCCESSFUL:
      case TranStatus.FAILED:
        return 'You can check the details in the transactions page';

      case TranStatus.TIMEOUT_ERROR:
        return 'Blockchain confirmations may take from a few minutes to an hour depending on network traffic. Your balance will update automatically once confirmed.';

      default:
        return 'Your transaction has been sent to the blockchain';
    }
  });

  protected orderStatus = computed(() => {
    switch (this.transactionStatus()) {
      case TranStatus.SUCCESSFUL:
        return 'Successful';

      case TranStatus.FAILED:
        return 'Failed';

      default:
        return 'Pending';
    }
  });

  // 後端回傳的 amount 已經減去平台手續費了，所以要呈現完成提款金額要把平台手續費(coinFee)加回來
  protected amount = computed(() => {
    const fee = this.item()?.data.fee;
    return fee ? this.item()?.data.amount.add(fee) : this.item()?.data.amount;
  });

  protected currency = computed(() => this.item()?.data.currency);

  protected txnHash = computed(() => {
    return this.walletService.shortenAddress(this.item()?.data.hashMsg);
  });

  protected txId = computed(() => {
    return this.walletService.shortenTxId(this.item()?.data.txId);
  });

  ngOnInit(): void {
    this.signalrService.isDepositProcessDialogExist = true;
    this.item.set(this.walletService.walletDetailsData || null);

    const orderStatus = this.item()?.orderStatus;
    const mappingDirt: Record<string, TranStatus> = {
      pending: TranStatus.TRANSACTION_SENT,
      success: TranStatus.SUCCESSFUL,
      fail: TranStatus.FAILED,
    };
    this.transactionStatus.set(mappingDirt[orderStatus!] ?? TranStatus.FAILED);

    this._registerTransactionStatusListener();
  }

  ngOnDestroy(): void {
    this.signalrService.isDepositProcessDialogExist = false;
  }

  private _createProgressTransition(startValue: number, endValue: number, durationMs: number): Observable<number> {
    const UPDATE_INTERVAL_MS = durationMs >= 60 * 1000 ? 250 : 50; // 每 250ms 更新一次，用於平滑過渡
    const totalSteps = Math.trunc(durationMs / UPDATE_INTERVAL_MS); // 直接捨去小數點
    const valueIncreasePerStep = (endValue - startValue) / totalSteps;
    if (totalSteps <= 0) return of(endValue);

    return interval(UPDATE_INTERVAL_MS).pipe(
      take(totalSteps),
      map(step => (step + 1 === totalSteps ? endValue : startValue + (step + 1) * valueIncreasePerStep)),
      map(calculatedValue => Math.min(calculatedValue, endValue)), // 確保在最後一步不會超過 endValue (雖然理論上不會，但可以防浮點數誤差)
      map(num => Math.trunc(num * 10000) / 10000), // 直接捨去第四位之後的小數點
    );
  }

  private _registerTransactionStatusListener(): void {
    if (this.transactionStatus() !== TranStatus.TRANSACTION_SENT) return;

    this.transactionStatus$.pipe(untilDestroyed(this)).subscribe(status => {
      switch (status) {
        case TranStatus.TRANSACTION_SENT:
          this._createProgressTransition(25, 75, 3 * 60 * 1000)
            .pipe(untilDestroyed(this), takeUntil(this.transactionStatus$.pipe(skip(1))))
            .subscribe(this.transactionProcess.set);
          break;

        case TranStatus.VERIFYING:
          this._createProgressTransition(75, 100, 3 * 1000)
            .pipe(untilDestroyed(this), takeUntil(this.transactionStatus$.pipe(skip(1))))
            .subscribe({
              next: num => this.transactionProcess.set(num),
              complete: () => this.transactionStatus.set(TranStatus.CREDITED),
            });
          break;

        case TranStatus.CREDITED:
          timer(3 * 1000).subscribe(() => this.transactionStatus.set(TranStatus.SUCCESSFUL));
          break;

        default:
          break;
      }
    });

    // Web3 Transaction Polling Check
    const isCompleted = (result: ResponseData<boolean>) => !!result?.data;
    const POLLING_SECOND_GAP = 5; // 輪詢間隔秒數
    interval(POLLING_SECOND_GAP * 1000)
      .pipe(
        untilDestroyed(this),
        takeUntil(this.transactionStatus$.pipe(skip(1))),
        map(count => (count + 1) * POLLING_SECOND_GAP), // 轉成秒數
        map(minutes => <HasCryptoDepositParam>{ currency: this.currency(), amount: this.amount(), timeRange: minutes }),
        switchMap(params => this.historyApi.hasCryptoDeposit(params)), // 確保在每次新的輪詢開始前，如果上一個請求還沒回來，會被取消 (防止請求堆疊)
        takeWhile(result => !isCompleted(result), true), // 檢查 API 結果，只要未完成就繼續 (true)
        filter(result => isCompleted(result)),
        timeout(10 * 60 * 1000),
      )
      .subscribe({
        next: () => this.transactionStatus.set(TranStatus.VERIFYING),
        error: err => {
          if (err.name === 'TimeoutError') {
            this.transactionStatus.set(TranStatus.TIMEOUT_ERROR);
          }
        },
      });
  }

  protected copyTxnHash(): void {
    this.walletService.copy(this.item()?.data.hashMsg, 'Copied TXN Hash');
  }

  protected copyTxId(): void {
    this.walletService.copy(this.item()?.data.txId, 'Copied ID');
  }

  protected close(): void {
    this.walletService.closeWallet();
  }

  protected jumpToTransactions(): void {
    this.router
      .navigate([this.appService.languageCode, 'transactions', 'deposit'], {
        queryParams: {},
        fragment: undefined,
      })
      .then(() => {
        this.close();
      });
  }
}
